import { useState, useEffect, useRef, useCallback } from "react";
import api from "../api/api";
import { useNavigate } from "react-router-dom";

/* ── Icons as emoji/unicode — always render, zero dependency ── */
const IcSend   = () => <span style={{fontSize:16,lineHeight:1}}>➤</span>;
const IcAttach = () => <span style={{fontSize:16,lineHeight:1}}>📎</span>;
const IcPlus   = () => <span style={{fontSize:14,lineHeight:1,fontWeight:700}}>+</span>;
const IcZap    = () => <span style={{fontSize:12,lineHeight:1}}>⚡</span>;
const IcMenu   = () => <span style={{fontSize:16,lineHeight:1}}>☰</span>;
const IcImg    = () => <span style={{fontSize:12,lineHeight:1}}>🖼</span>;
const IcDl     = () => <span style={{fontSize:11,lineHeight:1}}>⬇</span>;
const IcEdit   = () => <span style={{fontSize:11,lineHeight:1}}>✏️</span>;
const IcRedo   = () => <span style={{fontSize:11,lineHeight:1}}>↺</span>;
const IcClose  = () => <span style={{fontSize:13,lineHeight:1,fontWeight:700}}>✕</span>;
const IcExpand = () => <span style={{fontSize:11,lineHeight:1}}>⛶</span>;
const IcLogout = () => <span style={{fontSize:12,lineHeight:1}}>↪</span>;
const IcSpark  = () => <span style={{fontSize:13,lineHeight:1}}>✦</span>;
const IcAI     = () => <span style={{fontSize:13,lineHeight:1}}>◈</span>;
const IcNew    = () => <span style={{fontSize:13,lineHeight:1,fontWeight:700}}>+</span>;
const IcX      = () => <span style={{fontSize:11,lineHeight:1,fontWeight:700}}>✕</span>;

const LogoMark = ({ size = 28, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs><filter id="lg2"><feColorMatrix type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="r"/><feOffset in="r" dx="-1.5" dy="0" result="rO"/><feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="g"/><feOffset in="g" dx="1.5" dy="0" result="gO"/><feMerge><feMergeNode in="rO"/><feMergeNode in="gO"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
    <g filter="url(#lg2)" stroke={color} fill="none" strokeLinecap="round">
      <path d="M50 18 A32 32 0 0 1 82 50" strokeWidth="5"/><path d="M50 82 A32 32 0 0 1 18 50" strokeWidth="5"/>
      <line x1="14" y1="50" x2="86" y2="50" strokeWidth="4"/><line x1="50" y1="14" x2="50" y2="86" strokeWidth="4"/>
      <circle cx="14" cy="50" r="4" fill={color} stroke="none"/><circle cx="86" cy="50" r="4" fill={color} stroke="none"/>
      <circle cx="50" cy="14" r="4" fill={color} stroke="none"/><circle cx="50" cy="86" r="4" fill={color} stroke="none"/>
      <polygon points="50,40 43,56 57,56" fill={color} stroke="none"/>
      <circle cx="74" cy="26" r="4" fill={color} stroke="none"/><circle cx="26" cy="74" r="4" fill={color} stroke="none"/>
    </g>
  </svg>
);

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
:root{
  --blue:#2563eb;--blue2:#1d4ed8;--sky:#0ea5e9;
  --blu-lt:#eff6ff;--blu-mid:#dbeafe;--ring:rgba(37,99,235,0.14);
  --white:#ffffff;--bg:#f1f5fd;--bg2:#e8eefb;
  --border:rgba(37,99,235,0.10);--border2:rgba(37,99,235,0.20);
  --txt:#0f172a;--txt2:#334155;--txt3:#94a3b8;
  --ok:#10b981;--err:#ef4444;--warn:#f59e0b;
  --grad:linear-gradient(135deg,#2563eb,#0ea5e9);
  --grad2:linear-gradient(135deg,#1d4ed8,#2563eb 60%,#0ea5e9);
  --gsoft:linear-gradient(135deg,#eff6ff,#e0f2fe);
  --sh-xs:0 1px 3px rgba(37,99,235,0.06);
  --sh-sm:0 3px 12px rgba(37,99,235,0.10);
  --sh-md:0 6px 24px rgba(37,99,235,0.14);
  --sh-lg:0 16px 48px rgba(37,99,235,0.17);
  --r:6px;--r2:10px;--r3:14px;--r4:18px;--r5:22px;--rpill:999px;
}
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
html,body,#root{height:100%;font-family:'Poppins',sans-serif;background:var(--bg);color:var(--txt);-webkit-font-smoothing:antialiased;overflow:hidden;}
::-webkit-scrollbar{width:3px;height:3px;}
::-webkit-scrollbar-track{background:transparent;}
::-webkit-scrollbar-thumb{background:var(--border2);border-radius:99px;}

.mesh{position:fixed;inset:0;z-index:0;pointer-events:none;overflow:hidden;}
.mesh::before{content:'';position:absolute;width:600px;height:600px;border-radius:50%;background:radial-gradient(circle,rgba(37,99,235,0.07),transparent 70%);top:-200px;right:-150px;animation:d1 14s ease-in-out infinite alternate;}
.mesh::after{content:'';position:absolute;width:400px;height:400px;border-radius:50%;background:radial-gradient(circle,rgba(14,165,233,0.06),transparent 70%);bottom:-150px;left:-100px;animation:d2 18s ease-in-out infinite alternate;}
@keyframes d1{from{transform:translate(0,0)}to{transform:translate(-50px,50px)}}
@keyframes d2{from{transform:translate(0,0)}to{transform:translate(50px,-40px)}}

.shell{position:relative;z-index:1;display:flex;height:100vh;width:100vw;overflow:hidden;}

/* ── SIDEBAR ── */
.sb{width:240px;min-width:240px;background:rgba(255,255,255,0.82);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;box-shadow:3px 0 20px rgba(37,99,235,0.04);flex-shrink:0;transition:transform .3s cubic-bezier(.4,0,.2,1);}
.sb-brand{display:flex;align-items:center;gap:9px;padding:13px 14px 11px;border-bottom:1px solid var(--border);flex-shrink:0;}
.sb-logo{width:32px;height:32px;border-radius:var(--r2);background:var(--grad);display:flex;align-items:center;justify-content:center;box-shadow:var(--sh-sm);flex-shrink:0;animation:glow 3s ease-in-out infinite;padding:4px;}
@keyframes glow{0%,100%{box-shadow:var(--sh-sm)}50%{box-shadow:var(--sh-md),0 0 14px rgba(37,99,235,.18)}}
.sb-bname{font-size:16px;font-weight:800;color:var(--txt);letter-spacing:-.3px;}
.sb-bname b{color:var(--blue);}
.sb-btag{font-size:8px;color:var(--txt3);font-weight:500;letter-spacing:.4px;text-transform:uppercase;}
.sb-cred{margin:10px 10px 0;padding:12px 13px;background:var(--gsoft);border:1px solid var(--border2);border-radius:var(--r4);flex-shrink:0;}
.sb-cred-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;}
.sb-clbl{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:var(--blue);display:flex;align-items:center;gap:4px;}
.sb-cbadge{padding:1px 7px;border-radius:var(--rpill);background:var(--blue);color:white;font-size:8px;font-weight:700;}
.sb-cnum{font-size:32px;font-weight:800;color:var(--txt);line-height:1;letter-spacing:-1.5px;}
.sb-cnum small{font-size:11px;color:var(--txt3);font-weight:500;letter-spacing:0;margin-left:3px;}
.sb-cbar{height:3px;background:rgba(37,99,235,.1);border-radius:99px;overflow:hidden;margin:8px 0 10px;}
.sb-cfill{height:100%;background:var(--grad);border-radius:99px;transition:width .7s ease;box-shadow:0 0 6px rgba(37,99,235,.3);}
.sb-buybtn{width:100%;padding:9px;background:var(--grad2);border:none;border-radius:var(--r2);color:white;font-family:'Poppins',sans-serif;font-size:12px;font-weight:700;cursor:pointer;transition:all .2s;box-shadow:var(--sh-sm);display:flex;align-items:center;justify-content:center;gap:6px;}
.sb-buybtn:hover{transform:translateY(-1px);box-shadow:var(--sh-md);}
.sb-hist{flex:1;overflow-y:auto;padding:8px;}
.sb-hlbl{font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:var(--txt3);padding:2px 5px 8px;}
.hi{display:flex;align-items:center;gap:8px;padding:7px 8px;border-radius:var(--r2);cursor:pointer;transition:all .18s;border:1px solid transparent;margin-bottom:1px;}
.hi:hover{background:var(--blu-lt);border-color:var(--border2);}
.hi.on{background:var(--blu-lt);border-color:var(--border2);}
.hi-th{width:28px;height:28px;border-radius:var(--r);background:var(--bg2);border:1px solid var(--border);overflow:hidden;flex-shrink:0;display:flex;align-items:center;justify-content:center;color:var(--txt3);}
.hi-th img{width:100%;height:100%;object-fit:cover;}
.hi-t{font-size:11px;font-weight:600;color:var(--txt);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.hi-s{font-size:9px;color:var(--txt3);}
.sb-user{padding:8px 10px;border-top:1px solid var(--border);flex-shrink:0;}
.sb-urow{display:flex;align-items:center;gap:8px;padding:8px 10px;background:rgba(255,255,255,.7);border:1px solid var(--border);border-radius:var(--r2);margin-bottom:7px;}
.sb-uav{width:28px;height:28px;border-radius:50%;background:var(--grad);color:white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;flex-shrink:0;box-shadow:var(--sh-sm);}
.sb-uname{font-size:11px;font-weight:700;color:var(--txt);}
.sb-uemail{font-size:9px;color:var(--txt3);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.sb-lobtn{width:100%;padding:7px;background:transparent;border:1px solid var(--border);border-radius:var(--r2);color:var(--txt3);font-family:'Poppins',sans-serif;font-size:10px;font-weight:600;cursor:pointer;transition:all .2s;display:flex;align-items:center;justify-content:center;gap:5px;}
.sb-lobtn:hover{border-color:var(--err);color:var(--err);}

/* ── MAIN ── */
.main{flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0;}
.topbar{height:52px;padding:0 16px;background:rgba(255,255,255,.82);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;box-shadow:0 1px 8px rgba(37,99,235,.04);}
.tb-l{display:flex;align-items:center;gap:8px;}
.tb-r{display:flex;align-items:center;gap:6px;}
.hamburger{display:none;width:32px;height:32px;background:var(--white);border:1px solid var(--border);border-radius:var(--r2);align-items:center;justify-content:center;color:var(--txt2);cursor:pointer;transition:all .2s;flex-shrink:0;}
.hamburger:hover{border-color:var(--blue);color:var(--blue);}
.tb-title{font-size:13px;font-weight:700;color:var(--txt);white-space:nowrap;}
.tb-btn{padding:5px 11px;background:var(--white);border:1px solid var(--border);border-radius:var(--rpill);color:var(--txt2);font-family:'Poppins',sans-serif;font-size:11px;font-weight:600;cursor:pointer;transition:all .2s;white-space:nowrap;display:flex;align-items:center;gap:4px;}
.tb-btn:hover{border-color:var(--blue);color:var(--blue);background:var(--blu-lt);}
.tb-btn.p{background:var(--grad);border:none;color:white;box-shadow:var(--sh-sm);}
.tb-btn.p:hover{box-shadow:var(--sh-md);transform:translateY(-1px);}

/* ── MESSAGES ── */
.msgs{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:14px;}
.welcome{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100%;gap:14px;text-align:center;padding:20px;}
.w-logo{width:68px;height:68px;border-radius:50%;background:var(--grad);display:flex;align-items:center;justify-content:center;box-shadow:0 12px 40px rgba(37,99,235,.28),0 0 0 10px rgba(37,99,235,.06);animation:float3d 3.5s ease-in-out infinite;padding:10px;}
.w-logo-sh{width:48px;height:10px;background:radial-gradient(ellipse,rgba(37,99,235,.2),transparent 70%);border-radius:50%;margin-top:2px;animation:shp 3.5s ease-in-out infinite;}
@keyframes float3d{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
@keyframes shp{0%,100%{width:48px;opacity:.7}50%{width:32px;opacity:.3}}
.w-title{font-size:19px;font-weight:800;color:var(--txt);letter-spacing:-.4px;line-height:1.2;}
.w-title span{color:var(--blue);}
.w-sub{font-size:12px;color:var(--txt3);max-width:320px;line-height:1.7;font-weight:400;}
.w-tip{display:flex;align-items:center;gap:7px;padding:8px 14px;background:var(--blu-lt);border:1px solid var(--border2);border-radius:var(--rpill);font-size:11px;color:var(--blue);font-weight:600;}

/* suggestion chips */
.sug-row{display:flex;flex-wrap:wrap;gap:7px;justify-content:center;max-width:500px;}
.sug{padding:7px 13px;background:rgba(255,255,255,.9);border:1px solid var(--border);border-radius:var(--rpill);cursor:pointer;font-size:11px;color:var(--txt2);font-weight:500;transition:all .2s;white-space:nowrap;box-shadow:var(--sh-xs);}
.sug:hover{border-color:var(--blue);color:var(--blue);background:var(--blu-lt);transform:translateY(-1px);}

.msg{display:flex;gap:8px;animation:pop .28s ease;}
.msg.me{flex-direction:row-reverse;}
@keyframes pop{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
.m-av{width:28px;height:28px;border-radius:50%;background:var(--bg2);border:1px solid var(--border);display:flex;align-items:center;justify-content:center;color:var(--txt3);flex-shrink:0;}
.msg.me .m-av{background:var(--grad);border:none;color:white;box-shadow:var(--sh-sm);font-size:10px;font-weight:800;}
.m-body{max-width:70%;}
.msg.me .m-body{display:flex;flex-direction:column;align-items:flex-end;}
.bubble{padding:9px 13px;background:rgba(255,255,255,.88);border:1px solid var(--border);border-radius:var(--r4);border-top-left-radius:3px;font-size:12.5px;color:var(--txt2);line-height:1.6;backdrop-filter:blur(6px);box-shadow:var(--sh-xs);}
.msg.me .bubble{background:var(--grad2);border:none;color:white;border-top-left-radius:var(--r4);border-top-right-radius:3px;box-shadow:var(--sh-sm);}
.bubble.err{background:#fff5f5;border-color:#fecaca;color:var(--err);}

/* user image attachment preview in bubble */
.attached-img-preview{
  display:flex;align-items:center;gap:8px;
  padding:6px 8px;
  background:rgba(255,255,255,.15);
  border-radius:var(--r2);
  margin-bottom:6px;
  border:1px solid rgba(255,255,255,.2);
}
.attached-img-preview img{width:36px;height:36px;border-radius:var(--r);object-fit:cover;flex-shrink:0;}
.attached-img-label{font-size:10px;color:rgba(255,255,255,.8);font-weight:500;}

.m-ts{font-size:9px;color:var(--txt3);margin-top:3px;padding:0 3px;font-weight:500;}
.msg.me .m-ts{text-align:right;}
.dots{display:flex;gap:4px;padding:2px 1px;}
.dot{width:6px;height:6px;background:var(--txt3);border-radius:50%;animation:db 1.3s infinite ease-in-out;}
.dot:nth-child(2){animation-delay:.15s}.dot:nth-child(3){animation-delay:.3s}
@keyframes db{0%,80%,100%{transform:scale(.5);opacity:.4}40%{transform:scale(1);opacity:1}}
.gen-row{margin-top:7px;display:flex;align-items:center;gap:7px;font-size:10px;color:var(--txt3);font-weight:500;}
.gen-track{flex:1;height:2px;background:var(--border);border-radius:99px;overflow:hidden;}
.gen-fill{height:100%;background:var(--grad);border-radius:99px;animation:gs 2s ease-in-out infinite;}
@keyframes gs{0%{width:0%;margin-left:0%}50%{width:60%;margin-left:18%}100%{width:0%;margin-left:100%}}

/* image result card */
.img-card{margin-top:8px;background:var(--white);border:1px solid var(--border);border-radius:var(--r4);overflow:hidden;box-shadow:var(--sh-sm);width:240px;transition:transform .25s,box-shadow .25s;}
.img-card:hover{transform:translateY(-2px);box-shadow:var(--sh-md);}
.img-box{position:relative;overflow:hidden;aspect-ratio:1;cursor:pointer;width:100%;}
.img-box img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .4s ease;}
.img-box:hover img{transform:scale(1.05);}
.img-hov{position:absolute;inset:0;background:linear-gradient(to top,rgba(37,99,235,.65) 0%,transparent 55%);opacity:0;transition:opacity .25s;display:flex;flex-direction:column;justify-content:flex-end;padding:10px;}
.img-box:hover .img-hov{opacity:1;}
.img-hov-t{font-size:10px;color:rgba(255,255,255,.92);font-weight:500;line-height:1.35;}
.img-exp{position:absolute;top:7px;right:7px;width:24px;height:24px;border-radius:var(--rpill);background:rgba(255,255,255,.2);backdrop-filter:blur(4px);border:1px solid rgba(255,255,255,.3);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .25s;color:white;}
.img-box:hover .img-exp{opacity:1;}
.img-acts{display:flex;gap:5px;padding:8px 9px;background:var(--bg);border-top:1px solid var(--border);}
.ia{flex:1;padding:6px 7px;background:var(--white);border:1px solid var(--border);border-radius:var(--r2);color:var(--txt3);font-family:'Poppins',sans-serif;font-size:10px;font-weight:600;cursor:pointer;transition:all .2s;display:flex;align-items:center;justify-content:center;gap:3px;}
.ia:hover{border-color:var(--blue);color:var(--blue);background:var(--blu-lt);}
.ia.p{background:var(--grad);border:none;color:white;box-shadow:var(--sh-xs);}
.ia.p:hover{box-shadow:var(--sh-sm);transform:translateY(-1px);}
.ia:disabled{opacity:.4;cursor:not-allowed;}

/* ══════════════════════════════
   INPUT ZONE — single unified
══════════════════════════════ */
.iz{
  padding:10px 16px 12px;
  background:rgba(255,255,255,.9);
  backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);
  border-top:1px solid var(--border);
  flex-shrink:0;
  box-shadow:0 -2px 14px rgba(37,99,235,.04);
}

/* image attachment preview strip (above input) */
.attach-preview{
  display:flex;align-items:center;gap:8px;
  padding:6px 10px 8px;
}
.attach-thumb{
  position:relative;
  width:48px;height:48px;border-radius:var(--r2);overflow:hidden;
  border:1.5px solid var(--border2);flex-shrink:0;
  box-shadow:var(--sh-sm);
}
.attach-thumb img{width:100%;height:100%;object-fit:cover;display:block;}
.attach-rm{
  position:absolute;top:-5px;right:-5px;
  width:16px;height:16px;border-radius:50%;
  background:var(--err);color:white;
  border:1.5px solid white;
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;font-size:8px;
  box-shadow:0 1px 4px rgba(0,0,0,.2);
}
.attach-meta{flex:1;}
.attach-name{font-size:11px;font-weight:600;color:var(--txt);}
.attach-hint{font-size:9.5px;color:var(--txt3);}

/* strength row (compact, shows when image attached) */
.str-strip{
  display:flex;align-items:center;gap:8px;
  padding:0 10px 8px;
}
.str-l{font-size:10px;color:var(--txt3);font-weight:600;white-space:nowrap;}
.str-sl{flex:1;-webkit-appearance:none;height:3px;border-radius:99px;background:var(--border);outline:none;cursor:pointer;}
.str-sl::-webkit-slider-thumb{-webkit-appearance:none;width:13px;height:13px;border-radius:50%;background:var(--blue);box-shadow:0 0 0 2px rgba(37,99,235,.2);cursor:pointer;}
.str-v{font-size:10.5px;color:var(--blue);font-weight:700;min-width:28px;text-align:right;}

/* the actual input row */
.in-row{
  display:flex;align-items:center;gap:6px;
  background:var(--white);border:1.5px solid var(--border2);
  border-radius:var(--r5);
  padding:5px 5px 5px 6px;
  transition:border-color .2s,box-shadow .2s;
  box-shadow:var(--sh-sm);
  min-height:52px;
}
.in-row:focus-within{border-color:var(--blue);box-shadow:0 0 0 3px var(--ring);}

/* attach button — Claude-style, always visible */
.attach-btn{
  width:36px;height:36px;border-radius:var(--r2);
  background:var(--bg2);
  border:1.5px solid var(--border2);
  color:var(--blue);
  cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  transition:all .2s;flex-shrink:0;
  position:relative;
}
.attach-btn:hover{background:var(--blu-lt);border-color:var(--blue);box-shadow:var(--sh-sm);}
.attach-btn.active{color:white;background:var(--grad);border-color:transparent;box-shadow:var(--sh-sm);}

/* paste hint badge */
.paste-hint{
  position:absolute;top:-26px;left:50%;transform:translateX(-50%);
  background:var(--txt);color:white;
  font-size:9px;font-weight:600;
  padding:3px 8px;border-radius:var(--rpill);
  white-space:nowrap;pointer-events:none;
  opacity:0;transition:opacity .2s;
  box-shadow:var(--sh-sm);
}
.attach-btn:hover .paste-hint{opacity:1;}

.chat-in{
  flex:1;background:transparent;border:none;outline:none;
  color:var(--txt);font-family:'Poppins',sans-serif;
  font-size:13px;resize:none;max-height:80px;
  padding:9px 6px;line-height:1.5;align-self:center;
}
.chat-in::placeholder{color:var(--txt3);}
.chat-in:disabled{cursor:not-allowed;}

/* send button — always fully visible */
.send-btn{
  width:40px;height:40px;border-radius:var(--r3);
  background:var(--grad2);border:none;
  color:white;cursor:pointer;
  transition:all .2s;
  display:flex;align-items:center;justify-content:center;
  flex-shrink:0;
  box-shadow:0 4px 14px rgba(37,99,235,.35);
  min-width:40px;
}
.send-btn:hover:not(:disabled){transform:scale(1.08) rotate(-5deg);box-shadow:0 6px 20px rgba(37,99,235,.45);}
.send-btn:disabled{opacity:.55;cursor:not-allowed;background:var(--grad2);}

.in-hint{margin-top:5px;font-size:9px;color:var(--txt3);text-align:center;font-weight:500;}

/* drag-over overlay */
.drag-over .in-row{border-color:var(--blue);box-shadow:0 0 0 3px var(--ring),0 0 20px rgba(37,99,235,.12);background:var(--blu-lt);}

/* ── LIGHTBOX ── */
.lb-bg{position:fixed;inset:0;background:rgba(15,23,42,.88);backdrop-filter:blur(20px);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;animation:fdi .22s ease;}
@keyframes fdi{from{opacity:0}to{opacity:1}}
.lb-box{position:relative;display:flex;flex-direction:column;align-items:center;gap:14px;max-width:90vw;animation:sci .32s cubic-bezier(.34,1.56,.64,1);}
@keyframes sci{from{opacity:0;transform:scale(.88)}to{opacity:1;transform:scale(1)}}
.lb-img{max-width:80vw;max-height:74vh;border-radius:var(--r5);box-shadow:0 40px 80px rgba(0,0,0,.5);}
.lb-x{position:absolute;top:-12px;right:-12px;width:30px;height:30px;border-radius:50%;background:white;border:1px solid var(--border);color:var(--txt2);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .2s;}
.lb-x:hover{border-color:var(--err);color:var(--err);}
.lb-acts{display:flex;gap:8px;flex-wrap:wrap;justify-content:center;}
.lb-btn{padding:8px 16px;background:rgba(255,255,255,.12);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,.2);border-radius:var(--rpill);color:white;font-family:'Poppins',sans-serif;font-size:11px;font-weight:600;cursor:pointer;transition:all .2s;display:flex;align-items:center;gap:6px;}
.lb-btn:hover{background:rgba(255,255,255,.22);}
.lb-btn.b{background:var(--grad);border:none;box-shadow:var(--sh-sm);}
.lb-btn.b:hover{transform:translateY(-1px);box-shadow:var(--sh-md);}

/* ── MODAL ── */
.mod-bg{position:fixed;inset:0;background:rgba(15,23,42,.7);backdrop-filter:blur(12px);z-index:9000;display:flex;align-items:center;justify-content:center;padding:16px;animation:fdi .22s ease;}
.modal{background:rgba(255,255,255,.97);border:1px solid var(--border);border-radius:var(--r5);padding:24px;width:100%;max-width:380px;box-shadow:var(--sh-lg);animation:sci .28s cubic-bezier(.34,1.56,.64,1);}
.mod-t{font-size:19px;font-weight:800;color:var(--txt);letter-spacing:-.3px;margin-bottom:4px;}
.mod-s{font-size:12px;color:var(--txt3);margin-bottom:18px;}
.pack-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:18px;}
.pk{padding:13px;background:var(--bg);border:1px solid var(--border);border-radius:var(--r4);cursor:pointer;transition:all .2s;position:relative;text-align:left;}
.pk:hover{border-color:var(--blue);transform:translateY(-1px);box-shadow:var(--sh-sm);}
.pk.on{background:var(--blu-lt);border-color:var(--blue);}
.pk-hot{position:absolute;top:-8px;right:8px;background:var(--grad);color:white;font-size:8px;font-weight:800;padding:2px 7px;border-radius:var(--rpill);letter-spacing:.4px;text-transform:uppercase;}
.pk-n{font-size:24px;font-weight:800;color:var(--txt);line-height:1;}
.pk-l{font-size:9px;color:var(--txt3);font-weight:600;letter-spacing:.3px;margin:2px 0 5px;}
.pk-p{font-size:14px;font-weight:700;color:var(--blue);}
.mod-cta{width:100%;padding:11px;background:var(--grad2);border:none;border-radius:var(--r4);color:white;font-family:'Poppins',sans-serif;font-size:14px;font-weight:700;cursor:pointer;margin-bottom:8px;box-shadow:var(--sh-sm);transition:all .2s;}
.mod-cta:hover:not(:disabled){box-shadow:var(--sh-md);transform:translateY(-1px);}
.mod-cta:disabled{opacity:.5;cursor:not-allowed;}
.mod-cn{width:100%;padding:9px;background:transparent;border:1px solid var(--border);border-radius:var(--r4);color:var(--txt3);font-family:'Poppins',sans-serif;font-size:12px;font-weight:600;cursor:pointer;transition:all .2s;}
.mod-cn:hover{border-color:var(--border2);color:var(--txt2);}

/* ── TOASTS ── */
.toasts{position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:99999;display:flex;flex-direction:column;gap:6px;align-items:center;pointer-events:none;}
.toast{padding:8px 18px;background:rgba(255,255,255,.97);border:1px solid var(--border);border-radius:var(--rpill);font-family:'Poppins',sans-serif;font-size:11px;font-weight:600;color:var(--txt);box-shadow:var(--sh-md);display:flex;align-items:center;gap:7px;animation:ti .28s ease,to .28s ease 2.7s forwards;white-space:nowrap;}
.toast.success{border-left:3px solid var(--ok);}
.toast.error{border-left:3px solid var(--err);}
.toast.warn{border-left:3px solid var(--warn);}
.tdot{width:6px;height:6px;border-radius:50%;flex-shrink:0;}
.toast.success .tdot{background:var(--ok);}
.toast.error .tdot{background:var(--err);}
.toast.warn .tdot{background:var(--warn);}
@keyframes ti{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes to{to{opacity:0;transform:translateY(6px)}}
.mob-ov{display:none;position:fixed;inset:0;background:rgba(15,23,42,.4);backdrop-filter:blur(2px);z-index:199;}
.mob-ov.show{display:block;}

/* ── RESPONSIVE ── */
@media(max-width:860px){
  .sb{position:fixed;left:0;top:0;bottom:0;z-index:200;transform:translateX(-100%);transition:transform .3s cubic-bezier(.4,0,.2,1);box-shadow:var(--sh-lg);width:260px;}
  .sb.open{transform:translateX(0);}
  .hamburger{display:flex;}
  .msgs{padding:12px;}
  .iz{padding:8px 12px 10px;}
  .m-body{max-width:84%;}
  .img-card{width:min(240px,100%);}
  .topbar{padding:0 12px;}
}
@media(max-width:480px){
  .topbar{padding:0 10px;height:48px;}
  .tb-title{display:none;}
  .msgs{padding:10px 8px;gap:10px;}
  .iz{padding:7px 10px 9px;}
  .m-body{max-width:92%;}
  .img-card{width:100%;max-width:280px;}
  .tb-btn:not(.p){display:none;}
  .tb-btn.p{padding:5px 10px;font-size:10.5px;}
  .w-title{font-size:16px;}
  .w-sub{font-size:11px;}
  .w-tip{font-size:10px;padding:6px 12px;}
  .sug-row{gap:5px;}
  .sug{font-size:10px;padding:6px 10px;}
  .bubble{font-size:12px;padding:8px 11px;}
  .chat-in{font-size:12.5px;}
  .attach-btn{width:32px;height:32px;}
  .send-btn{width:36px;height:36px;}
  .in-row{padding:3px 3px 3px 8px;min-height:48px;}
  .in-hint{font-size:8.5px;}
  .attach-preview{padding:5px 8px 6px;}
  .attach-thumb{width:40px;height:40px;}
  .attach-name{font-size:10.5px;}
  .attach-hint{font-size:9px;}
  .str-strip{padding:0 8px 6px;}
  .str-l{font-size:9.5px;}
  .str-v{font-size:10px;}
  .sb-cnum{font-size:26px;}
  .hi-t{font-size:10.5px;}
  .hi{padding:6px 6px;}
}
@media(max-width:360px){
  .msgs{padding:8px 6px;}
  .bubble{font-size:11.5px;}
  .img-card{max-width:100%;}
  .in-row{gap:4px;}
}
`;

/* ── helpers ── */
const BASE = "http://127.0.0.1:8000";
const mkUrl = p => !p ? "" : p.startsWith("http") ? p : BASE + (p.startsWith("/") ? p : "/" + p);

async function dlCanvas(url, name) {
  return new Promise((res, rej) => {
    const img = new Image(); img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      c.toBlob(b => {
        if (!b) { rej(new Error("blob")); return; }
        const u = URL.createObjectURL(b);
        const a = Object.assign(document.createElement("a"), { href: u, download: `${name}.png`, style: "display:none" });
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(u), 8000); res();
      }, "image/png");
    };
    img.onerror = () => rej(new Error("load")); img.src = url;
  });
}
async function dlFetch(url, name) {
  const r = await fetch(url, { mode: "cors" }); if (!r.ok) throw new Error(`${r.status}`);
  const b = await r.blob();
  const ext = { "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp" }[b.type] || "png";
  const u = URL.createObjectURL(b);
  const a = Object.assign(document.createElement("a"), { href: u, download: `${name}.${ext}`, style: "display:none" });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(u), 8000);
}
const tryDl = async (url, name) => { try { await dlCanvas(url, name); } catch { await dlFetch(url, name); } };
const toB64 = f => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
const exB64 = d => { if (!d) return ""; if (d.includes(",")) return d.split(",")[1]; return d; };
const relTime = ts => {
  if (!ts) return "Just now";
  const date = typeof ts === "number" ? new Date(ts) : new Date(ts);
  if (isNaN(date.getTime())) return "Just now";
  const d = Date.now() - date.getTime(), m = Math.floor(d / 60000), h = Math.floor(d / 3600000), dy = Math.floor(d / 86400000);
  if (m < 1) return "Just now"; if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`; if (dy < 7) return `${dy}d ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const QUALITY = ", ultra realistic, 8k uhd, professional photography, masterpiece, sharp focus, highly detailed";
const NEG = "blurry,ugly,deformed,extra limbs,bad anatomy,watermark,text,logo,cartoon,low quality,grainy";
const SUGS = [
  "Indian bride in gold lehenga, jewellery closeup",
  "Kerala backwaters, golden hour, cinematic",
  "Corporate portrait, dramatic lighting",
  "Luxury product on marble, commercial photo",
];
const PACKS = [{ n: 10, p: "₹399", l: "Starter" }, { n: 25, p: "₹799", l: "Popular", hot: true }, { n: 50, p: "₹1,299", l: "Pro" }, { n: 100, p: "₹1,999", l: "Best" }];
let _tid = 0;

export default function ChatDashboard() {
  const nav = useNavigate();
  const [user,     setUser]     = useState(null);
  const [credits,  setCredits]  = useState(0);
  const [msgs,     setMsgs]     = useState([]);
  const [input,    setInput]    = useState("");
  const [busy,     setBusy]     = useState(false);
  const [upload,   setUpload]   = useState(null);   // { preview, base64, name }
  const [strength, setStrength] = useState(0.85);
  const [lb,       setLb]       = useState(null);
  const [buyModal, setBuyModal] = useState(false);
  const [selPack,  setSelPack]  = useState(1);
  const [buyBusy,  setBuyBusy]  = useState(false);
  const [toasts,   setToasts]   = useState([]);
  const [dlBusy,   setDlBusy]   = useState(false);
  const [sidebar,  setSidebar]  = useState(false);
  const [hist,     setHist]     = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const endRef  = useRef(null);
  const inRef   = useRef(null);
  const fileRef = useRef(null);
  const izRef   = useRef(null);

  useEffect(() => {
    const s = localStorage.getItem("user");
    if (!s) { nav("/"); return; }
    const u = JSON.parse(s);
    if (!u?.id) { nav("/"); return; }
    setUser(u); setCredits(u.credits || 0);
    fetchCredits(u.id); fetchHistory(u.id);
  }, [nav]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => { inRef.current?.focus(); }, []);

  /* ── paste image from clipboard ── */
  useEffect(() => {
    const onPaste = async e => {
      const items = Array.from(e.clipboardData?.items || []);
      const imgItem = items.find(i => i.type.startsWith("image/"));
      if (!imgItem) return;
      e.preventDefault();
      const file = imgItem.getAsFile();
      if (file) { await loadFile(file); toast("Image pasted! Describe the transformation below."); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [loadFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const toast = (msg, type = "success") => {
    const id = ++_tid;
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3300);
  };

  const fetchCredits = async uid => { try { const r = await api.get(`/credits/${uid}`); setCredits(r.data.credits); } catch {} };
  const fetchHistory = async uid => {
    try {
      const r = await api.get(`/history/${uid}`);
      setHist((r.data || []).map(item => ({
        id: String(item.id),
        title: item.prompt?.replace(/\[img2img\] /, "").slice(0, 42) || "Generation",
        ts: item.created_at || Date.now(),
        thumb: item.image ? (item.image.startsWith("http") ? item.image : `http://127.0.0.1:8000${item.image}`) : null,
      })));
    } catch {}
  };

  const loadFile = useCallback(async file => {
    if (!file.type.startsWith("image/")) { toast("Upload an image file", "error"); return; }
    if (file.size > 10 * 1024 * 1024) { toast("Max 10MB", "error"); return; }
    const dataUrl = await toB64(file);
    setUpload({ preview: dataUrl, base64: exB64(dataUrl), name: file.name || "image.png" });
  }, []);

  const handleFile = useCallback(async e => {
    const f = e.target.files?.[0];
    if (!f) return;
    await loadFile(f);
    e.target.value = "";
  }, [loadFile]);

  /* drag & drop */
  const onDragOver = e => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = e => { e.preventDefault(); setDragOver(false); };
  const onDrop = useCallback(async e => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) { await loadFile(file); toast("Image dropped! Describe the transformation."); }
  }, [loadFile]);

  const clearUpload = () => setUpload(null);

  const loadAsEdit = url => {
    fetch(url, { mode: "cors" }).then(r => r.blob()).then(blob => {
      const reader = new FileReader();
      reader.onload = () => { const d = reader.result; setUpload({ preview: d, base64: exB64(d), name: "generated.png" }); };
      reader.readAsDataURL(blob);
    }).catch(() => toast("Could not load image", "error"));
    setSidebar(false);
    toast("Loaded for editing — describe your changes below");
  };

  const send = async () => {
    if (!input.trim() || busy || credits <= 0) return;
    const isImg2Img = !!upload;

    const um = {
      id: String(Date.now()), type: "me",
      content: input.trim(),
      attachedImg: isImg2Img ? upload.preview : null,
      ts: Date.now(),
    };
    setMsgs(p => [...p, um]);
    setInput("");
    setBusy(true);

    const lid = `l-${Date.now()}`;
    setMsgs(p => [...p, { id: lid, type: "ai", loading: true, ts: Date.now() }]);

    try {
      let res;
      if (isImg2Img) {
        res = await api.post("/generate-img2img", {
          user_id: user.id,
          prompt: um.content + QUALITY,
          image_base64: upload.base64,
          strength,
          negative_prompt: NEG,
          steps: 10,
          guidance_scale: 9.0,
        });
      } else {
        res = await api.post("/generate", {
          user_id: user.id,
          prompt: um.content + QUALITY,
          negative_prompt: NEG,
          steps: 10,
          guidance_scale: 7.0,
          width: 512, height: 512,
        });
      }

      setMsgs(p => p.filter(m => m.id !== lid));
      setMsgs(p => [...p, {
        id: `i-${Date.now()}`, type: "ai",
        content: isImg2Img ? "Here's your transformed image!" : "Here's your creation!",
        image: { id: Date.now(), url: mkUrl(res.data.image), prompt: um.content },
        ts: Date.now(),
      }]);
      setCredits(res.data.credits_left);
      toast("Done!");
      setHist(p => [{ id: String(Date.now()), title: um.content.slice(0, 42), ts: Date.now(), thumb: mkUrl(res.data.image) }, ...p.slice(0, 9)]);
      setUpload(null); // clear attachment after send

    } catch (err) {
      setMsgs(p => p.filter(m => m.id !== lid));
      const d = err.response?.data?.detail || "Generation failed — try again.";
      setMsgs(p => [...p, { id: `e-${Date.now()}`, type: "ai", content: d, err: true, ts: Date.now() }]);
      toast(d, "error");
    }
    setBusy(false);
  };

  const onKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  const handleDl = async img => {
    if (dlBusy) return; setDlBusy(true); toast("Downloading…");
    try { await tryDl(img.url, `pic9ine-${img.id}`); toast("Saved!"); }
    catch { window.open(img.url, "_blank"); toast("Opened in new tab", "warn"); }
    setDlBusy(false);
  };

  const buyCredits = async () => {
    setBuyBusy(true);
    try {
      const pk = PACKS[selPack];
      await api.post("/buy-credits", { user_id: user.id, credits: pk.n });
      await fetchCredits(user.id); setBuyModal(false); toast(`${pk.n} credits added!`);
    } catch { toast("Purchase failed", "error"); }
    setBuyBusy(false);
  };

  const newChat = () => { setMsgs([]); setActiveId(null); setUpload(null); setSidebar(false); };
  const logout  = () => { localStorage.removeItem("user"); nav("/"); };

  const pct = Math.min((credits / 20) * 100, 100);
  const ini = user?.email?.[0]?.toUpperCase() || "U";

  const isImg2Img = !!upload;

  return (
    <>
      <style>{CSS}</style>
      <div className="mesh" />
      <div className={`mob-ov ${sidebar ? "show" : ""}`} onClick={() => setSidebar(false)} />

      {/* TOASTS */}
      <div className="toasts">
        {toasts.map(t => <div key={t.id} className={`toast ${t.type}`}><span className="tdot" />{t.msg}</div>)}
      </div>

      {/* LIGHTBOX */}
      {lb && (
        <div className="lb-bg" onClick={() => setLb(null)}>
          <div className="lb-box" onClick={e => e.stopPropagation()}>
            <img className="lb-img" src={lb.url} alt={lb.prompt} crossOrigin="anonymous" />
            <button className="lb-x" onClick={() => setLb(null)}><IcClose /></button>
            <div className="lb-acts">
              <button className="lb-btn b" onClick={() => handleDl(lb)}><IcDl /> Download</button>
              <button className="lb-btn" onClick={() => { setInput(lb.prompt); setLb(null); inRef.current?.focus(); }}><IcRedo /> Redo</button>
              <button className="lb-btn" onClick={() => { loadAsEdit(lb.url); setLb(null); }}><IcEdit /> Edit</button>
            </div>
          </div>
        </div>
      )}

      {/* BUY MODAL */}
      {buyModal && (
        <div className="mod-bg" onClick={() => setBuyModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="mod-t">Buy Credits</div>
            <div className="mod-s">Credits never expire.</div>
            <div className="pack-grid">
              {PACKS.map((pk, i) => (
                <button key={i} className={`pk ${selPack === i ? "on" : ""}`} onClick={() => setSelPack(i)}>
                  {pk.hot && <span className="pk-hot">Popular</span>}
                  <div className="pk-n">{pk.n}</div>
                  <div className="pk-l">Credits · {pk.l}</div>
                  <div className="pk-p">{pk.p}</div>
                </button>
              ))}
            </div>
            <button className="mod-cta" onClick={buyCredits} disabled={buyBusy}>
              {buyBusy ? "Processing…" : `Get ${PACKS[selPack].n} Credits — ${PACKS[selPack].p}`}
            </button>
            <button className="mod-cn" onClick={() => setBuyModal(false)}>Maybe Later</button>
          </div>
        </div>
      )}

      <div className="shell">

        {/* SIDEBAR */}
        <aside className={`sb ${sidebar ? "open" : ""}`}>
          <div className="sb-brand">
            <div className="sb-logo"><LogoMark size={24} color="white" /></div>
            <div>
              <div className="sb-bname">Pic<b>9</b>ine</div>
              <div className="sb-btag">AI Image Studio</div>
            </div>
          </div>

          <div className="sb-cred">
            <div className="sb-cred-top">
              <span className="sb-clbl"><IcZap /> Credits</span>
              <span className="sb-cbadge">Active</span>
            </div>
            <div className="sb-cnum">{credits}<small>left</small></div>
            <div className="sb-cbar"><div className="sb-cfill" style={{ width: `${pct}%` }} /></div>
            <button className="sb-buybtn" onClick={() => setBuyModal(true)}><IcPlus /> Buy Credits</button>
          </div>

          <div className="sb-hist">
            <div className="sb-hlbl">Recent</div>
            <div className={`hi ${!activeId ? "on" : ""}`} onClick={newChat}>
              <div className="hi-th"><IcSpark /></div>
              <div><div className="hi-t">New Chat</div><div className="hi-s">Start fresh</div></div>
            </div>
            {hist.map(c => (
              <div key={c.id} className={`hi ${activeId === c.id ? "on" : ""}`} onClick={() => { setActiveId(c.id); setSidebar(false); }}>
                <div className="hi-th">{c.thumb ? <img src={c.thumb} alt="" /> : <IcImg />}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="hi-t">{c.title}</div>
                  <div className="hi-s">{relTime(c.ts)}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="sb-user">
            <div className="sb-urow">
              <div className="sb-uav">{ini}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="sb-uname">{user?.email?.split("@")[0]}</div>
                <div className="sb-uemail">{user?.email}</div>
              </div>
            </div>
            <button className="sb-lobtn" onClick={logout}><IcLogout /> Sign Out</button>
          </div>
        </aside>

        {/* MAIN */}
        <div className="main">

          {/* TOPBAR */}
          <div className="topbar">
            <div className="tb-l">
              <button className="hamburger" onClick={() => setSidebar(s => !s)}><IcMenu /></button>
              <div className="tb-title">AI Image Studio</div>
              {isImg2Img && (
                <span style={{ padding: "3px 9px", borderRadius: "var(--rpill)", fontSize: 9, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", background: "#fef9c3", color: "#854d0e", border: "1px solid #fde68a" }}>
                  Image → Image
                </span>
              )}
            </div>
            <div className="tb-r">
              <button className="tb-btn" onClick={newChat}><IcNew /> New</button>
              <button className="tb-btn p" onClick={() => setBuyModal(true)}><IcZap /> {credits}</button>
            </div>
          </div>

          {/* MESSAGES */}
          <div className="msgs" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
            {msgs.length === 0 ? (
              <div className="welcome">
                <div>
                  <div className="w-logo"><LogoMark size={44} color="white" /></div>
                  <div className="w-logo-sh" />
                </div>
                <div>
                  <div className="w-title">Create <span>anything</span></div>
                  <div className="w-sub" style={{ marginTop: 6 }}>
                    Describe an image, or attach a photo to transform it.
                    Paste an image with <strong>Ctrl+V</strong> or drag & drop.
                  </div>
                </div>
                <div className="w-tip">
                  <IcAttach />
                  <span>Attach an image → transforms it · No image → generates from text</span>
                </div>
                <div className="sug-row">
                  {SUGS.map((s, i) => (
                    <button key={i} className="sug" onClick={() => { setInput(s); inRef.current?.focus(); }}>{s}</button>
                  ))}
                </div>
              </div>
            ) : msgs.map(m => (
              <div key={m.id} className={`msg ${m.type === "me" ? "me" : ""}`}>
                <div className="m-av">{m.type === "me" ? ini : <IcAI />}</div>
                <div className="m-body">
                  <div className={`bubble ${m.err ? "err" : ""}`}>
                    {/* show attached image in user bubble */}
                    {m.attachedImg && (
                      <div className="attached-img-preview">
                        <img src={m.attachedImg} alt="attached" />
                        <span className="attached-img-label">Image attached</span>
                      </div>
                    )}
                    {!m.loading && m.content}
                    {m.loading && (
                      <>
                        <div className="dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
                        <div className="gen-row">
                          <span style={{ color: "var(--blue)", display: "flex" }}><IcSpark /></span>
                          <div className="gen-track"><div className="gen-fill" /></div>
                          <span>Generating…</span>
                        </div>
                      </>
                    )}
                    {m.image && (
                      <div className="img-card">
                        <div className="img-box" onClick={() => setLb(m.image)}>
                          <img src={m.image.url} alt={m.image.prompt} crossOrigin="anonymous" />
                          <div className="img-hov"><div className="img-hov-t">{m.image.prompt}</div></div>
                          <div className="img-exp"><IcExpand /></div>
                        </div>
                        <div className="img-acts">
                          <button className="ia" onClick={() => handleDl(m.image)} disabled={dlBusy}><IcDl /></button>
                          <button className="ia" onClick={() => loadAsEdit(m.image.url)}><IcEdit /> Edit</button>
                          <button className="ia p" onClick={() => { setInput(m.image.prompt); inRef.current?.focus(); }}><IcRedo /> Redo</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="m-ts">{relTime(m.ts)}</div>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {/* ══════ INPUT ZONE ══════ */}
          <div className={`iz ${dragOver ? "drag-over" : ""}`} ref={izRef}>

            {/* attached image preview strip */}
            {upload && (
              <>
                <div className="attach-preview">
                  <div className="attach-thumb">
                    <img src={upload.preview} alt="attachment" />
                    <button className="attach-rm" onClick={clearUpload}><IcX /></button>
                  </div>
                  <div className="attach-meta">
                    <div className="attach-name">{upload.name}</div>
                    <div className="attach-hint">Image attached — describe how to transform it</div>
                  </div>
                </div>
                {/* strength slider shown inline when image attached */}
                <div className="str-strip">
                  <span className="str-l">Strength</span>
                  <input type="range" className="str-sl" min="0.1" max="0.99" step="0.01"
                    value={strength} onChange={e => setStrength(parseFloat(e.target.value))} />
                  <span className="str-v">{Math.round(strength * 100)}%</span>
                </div>
              </>
            )}

            {/* single input row */}
            <div className="in-row">

              {/* attach button — Claude style */}
              <button
                className={`attach-btn ${upload ? "active" : ""}`}
                onClick={() => fileRef.current?.click()}
                title="Attach image (or paste with Ctrl+V)"
              >
                <IcAttach />
                <span className="paste-hint">Attach or Ctrl+V</span>
              </button>

              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />

              <textarea
                ref={inRef}
                className="chat-in"
                rows={1}
                placeholder={
                  credits <= 0 ? "No credits — buy more to continue" :
                  upload ? "Describe how to transform the image…" :
                  "Describe the image you want, or attach a photo to edit it…"
                }
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKey}
                disabled={credits <= 0 || busy}
              />

              <button
                className="send-btn"
                onClick={send}
                disabled={!input.trim() || busy || credits <= 0}
              >
                {busy
                  ? <div className="dots" style={{ gap: 3 }}><div className="dot" /><div className="dot" /><div className="dot" /></div>
                  : <IcSend />}
              </button>
            </div>

            <div className="in-hint">
              Enter to generate · Shift+Enter new line · Attach image for img2img · Ctrl+V to paste · 1 credit/image
            </div>
          </div>

        </div>
      </div>
    </>
  );
}